import React, {
    ReactElement,
    MouseEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
    ComponentProps,
} from 'react';
import useToggle from 'react-use/lib/useToggle';
import { buildImageSource, buildImageSourceSet } from '../../../utils/buildImageSource';
import { getIsMobile } from '../../../utils/getIsMobile';
import { getWindow } from '../../../utils/getWindow';
import { useDoubleClick } from '../../../utils/useDoubleClick';
import { useTranslation } from '../../../utils/useTranslation';
import { Carousel } from '../../carousel/Carousel';
import { CarouselIndicators } from '../../carousel/CarouselIndicators';
import { Skeleton } from '../../feedback/Skeleton';
import { IconBookmark } from '../../icon/IconBookmark';
import { IconBookmarkFilled } from '../../icon/IconBookmarkFilled';
import { IconClock } from '../../icon/IconClock';
import { IconClose } from '../../icon/IconClose';
import { IconDislike } from '../../icon/IconDislike';
import { IconDislikeFilled } from '../../icon/IconDislikeFilled';
import { IconKebab } from '../../icon/IconKebab';
import { IconLike } from '../../icon/IconLike';
import { IconShare } from '../../icon/IconShare';
import { IconButton } from '../../input/IconButton';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { TranslateBox } from '../../layout/TranslateBox';
import { Image } from '../../media/Image';
import { Retry } from '../../media/Retry';
import { Video } from '../../media/Video';
import { Typography } from '../../typography/Typography';
import { PostStatus } from '../constants';
import { ProductItemUI, ReactionState, type PostUI } from '../types';
import { AnimateFooter, AnimateFooterSkeleton } from './AnimateFooter/AnimateFooter';
import { Root, Header, Shadow, Content, PostMediaCover } from './styles';

const PRODUCT_BUTTON_ANIMATE_DELAY = 1500;

const makeTransition = (...styles: string[]) =>
    styles.map((style) => `${style} 200ms linear`).join(', ');

const makeMediaParams = (post: PostUI, index: number) => ({
    mediaId: post.content[index].id,
    mediaIndex: index,
});

export interface PostCardProps extends ComponentProps<typeof Box> {
    post: PostUI;
    index: number;
    onBookmark?: VoidFunction;
    onShare?: (params: { mediaId: string }) => void;
    onReaction: ({
        postNumber,
        reactionState,
    }: {
        postNumber: number;
        reactionState: PostUI['reactionState'];
    }) => void;
    onSubscribe?: (params: { postNumber: number }) => void;
    onClickOutClick: (params: { mediaId: string }) => void;
    onSlideChange: (params: { mediaId: string; mediaIndex: number }) => void;
    onExpand: (params: { mediaId: string; mediaIndex: number }) => void;
    onVideoMuteChange: (params: { mediaId: string; mute: boolean }) => void;
    onVideoEnd: (params: { mediaId: string }) => void;
    onVideoStart: (params: { mediaId: string }) => void;
    onVideoPlay: (params: { mediaId: string }) => void;
    onVideoPause: (params: { mediaId: string }) => void;
    onVisibilityChange: (params: {
        mediaId: string;
        isInViewport: boolean;
        postNumber: number;
    }) => void;
    renderMenu: (trigger: ReactElement) => ReactElement | null;
    onProductButtonClick: VoidFunction;
    onPostTextProductsClick: (product: ProductItemUI) => void;
    onPostTextCatalogClick: VoidFunction;
    onPostTextClickoutClick: VoidFunction;
    isProductButtonEnabled?: boolean;
    isVisible?: boolean;
    isCardActive?: boolean;
    // Велечина, которую мы должны отнять от максимальной высоты поста,
    // например высота футера в мобилке
    maxHeightOffset?: number;
    onStatusBadgeClick: () => void;
}

export const renderStatusBadge = (post: PostUI, onStatusBadgeClick: VoidFunction) => {
    const t = useTranslation();

    if (post.business) {
        return null;
    }

    if (post.status === PostStatus.MODERATION) {
        return (
            <Flex
                direction="row"
                position="absolute"
                zIndex={5}
                py={0.5}
                pr={1}
                pl={0.5}
                borderRadius="12px"
                backgroundColor="rgba(0, 0, 0, 0.24)"
                spacing={0.5}
                left={2}
                top={3}
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={onStatusBadgeClick}
            >
                <IconClock size={20} fill="white" />
                <Typography variant="caption" weight="medium" color="white">
                    {t('На модерации')}
                </Typography>
            </Flex>
        );
    }

    if (post.status === PostStatus.DECLINED) {
        return (
            <Flex
                direction="row"
                py={0.5}
                pr={1}
                pl={0.5}
                borderRadius="12px"
                backgroundColor="rgba(255, 91, 91, 1)"
                position="absolute"
                zIndex={5}
                spacing={0.5}
                left={2}
                top={3}
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={onStatusBadgeClick}
            >
                <Box
                    borderRadius="50%"
                    backgroundColor="rgba(255, 255, 255, 1)"
                    border="solid 1px transparent"
                >
                    <IconClose fill="rgba(255, 91, 91, 1)" size={14} />
                </Box>
                <Typography variant="caption" weight="medium" loose={true}>
                    {t('Отклонён')}
                </Typography>
            </Flex>
        );
    }

    return null;
};

export function PostCard(props: PostCardProps) {
    const {
        post,
        renderMenu,
        onProductButtonClick,
        isVisible = true,
        isCardActive = true,
        maxHeightOffset,
    } = props;
    const [isExpanded, toggleIsExpanded] = useToggle(false);
    const [isProductExpanded, toggleIsProductExpanded] = useToggle(false);
    const isProductExpandedRef = useRef(false);
    const isProductExpandedTimerIdRef = useRef<number | undefined>();
    const postRef = useRef<null | HTMLDivElement>(null);
    const isVisibleRef = useRef(false);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [isSoundControlContainerEnabled, setIsSoundControlContainerEnabled] = useState(false);
    const [soundContainer, setSoundContainer] = useState<null | HTMLDivElement>(null);

    const { isMobile } = getIsMobile();

    const onToggleProductButton = useCallback(
        (isExpanded?: boolean) => {
            if (!post.products.items.length) {
                return;
            }

            toggleIsProductExpanded(isExpanded);
        },
        [toggleIsProductExpanded, post],
    );

    const onShare = useCallback(
        () => props.onShare?.(makeMediaParams(post, activeIndex)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [post.content, activeIndex, props.onShare],
    );

    const onSubscribe = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            props.onSubscribe?.({ postNumber: props.index });
            event.stopPropagation();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.index, props.onSubscribe],
    );
    const onBookmark = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            props.onBookmark?.();
            event.stopPropagation();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.onBookmark],
    );

    const onReaction = useCallback(
        (reactionState: PostUI['reactionState']) => {
            props.onReaction({ postNumber: props.index, reactionState });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.index, props.onReaction],
    );

    const onDoubleClick = useDoubleClick(
        () => props.onReaction({ postNumber: props.index, reactionState: ReactionState.LIKE }),
        [props.index, props.onReaction],
    );

    const onClickOutClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            props.onClickOutClick?.(makeMediaParams(post, activeIndex));
            event.stopPropagation();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeIndex, post, props.onClickOutClick],
    );

    const onSlideChange = useCallback(
        (index: number) => {
            setActiveIndex(index);
            props.onSlideChange?.(makeMediaParams(post, index));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [post, props.onSlideChange],
    );

    const onExpand = useCallback(
        (event: MouseEvent<any>) => {
            event.stopPropagation();

            if (!isExpanded) {
                props.onExpand?.(makeMediaParams(post, activeIndex));
            }

            getWindow()?.clearTimeout(isProductExpandedTimerIdRef.current);

            if ((isProductExpanded && isExpanded) || (!isProductExpanded && !isExpanded)) {
                onToggleProductButton();
                toggleIsExpanded();

                return;
            }

            if (isProductExpanded && !isExpanded) {
                onToggleProductButton();

                return;
            }

            if (!isProductExpanded && isExpanded) {
                toggleIsExpanded();

                return;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            activeIndex,
            post,
            isExpanded,
            isProductExpanded,
            onToggleProductButton,
            toggleIsExpanded,
            props.onExpand,
        ],
    );

    const onVideoMuteChange = useCallback(
        (mute: boolean) =>
            props.onVideoMuteChange?.({ ...makeMediaParams(post, activeIndex), mute }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeIndex, post, props.onVideoMuteChange],
    );
    const onVideoEnd = useCallback(
        () => props.onVideoEnd?.(makeMediaParams(post, activeIndex)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeIndex, post, props.onVideoEnd],
    );
    const onVideoStart = useCallback(
        () => props.onVideoStart?.(makeMediaParams(post, activeIndex)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeIndex, post, props.onVideoStart],
    );
    const onVideoPlay = useCallback(
        () => props.onVideoPlay?.(makeMediaParams(post, activeIndex)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeIndex, post, props.onVideoPlay],
    );
    const onVideoPause = useCallback(
        () => props.onVideoPause?.(makeMediaParams(post, activeIndex)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeIndex, post, props.onVideoPause],
    );

    const [firstContent] = post.content;
    const ratio = Math.min(
        (firstContent?.media[0]?.width || 1) / (firstContent?.media[0]?.height || 1),
        1,
    );

    useEffect(() => {
        if (isVisible !== isVisibleRef.current) {
            isVisibleRef.current = isVisible;
            props.onVisibilityChange?.({
                ...makeMediaParams(post, activeIndex),
                isInViewport: isVisible,
                postNumber: props.index,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post, activeIndex, isVisibleRef.current, isVisible, props.onVisibilityChange]);

    useEffect(() => {
        isProductExpandedRef.current = isProductExpanded;
    }, [isProductExpanded]);

    useEffect(() => {
        if (!post.products.items.length) {
            return;
        }

        const prodectFirstExpandedTrigger = isMobile ? isCardActive : isVisible;

        if (!prodectFirstExpandedTrigger) {
            getWindow()?.clearTimeout(isProductExpandedTimerIdRef.current);

            return;
        }

        if (isProductExpandedTimerIdRef.current || isProductExpandedRef.current) {
            return;
        }

        const timerId = getWindow()?.setTimeout(() => {
            onToggleProductButton(true);
        }, PRODUCT_BUTTON_ANIMATE_DELAY);

        isProductExpandedTimerIdRef.current = timerId;

        return () => {
            getWindow()?.clearTimeout(timerId);
            isProductExpandedTimerIdRef.current = undefined;
        };
    }, [isCardActive, post, isVisible, isMobile, onToggleProductButton]);

    useEffect(() => {
        const currentClide = post.content[activeIndex];
        setIsSoundControlContainerEnabled(currentClide.type !== 'image');
    }, [activeIndex, post]);

    return (
        <Root ref={postRef} onClick={onDoubleClick}>
            <Header background="linear-gradient(180deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.00) 100%)">
                {isSoundControlContainerEnabled ? (
                    <TranslateBox
                        dataTestId={DataTestId.MUTE}
                        transition={makeTransition('transform')}
                        ref={(element) => {
                            setSoundContainer(element);
                        }}
                        zIndex={5}
                        trigger={isExpanded}
                        x1={44}
                        x2={4}
                    />
                ) : null}
                {renderMenu(
                    <Box transition={makeTransition('opacity')} opacity={isExpanded ? 1 : 0}>
                        <IconButton size="s" cursor={isExpanded ? 'pointer' : 'default'}>
                            <IconKebab color="#FFFFFF" dataTestId={DataTestId.POST_CARD_KEBAB} />
                        </IconButton>
                    </Box>,
                )}
                {/* Заглушка, чтобы кебаб с opacity 0 не кликался */}
                {!isExpanded && <Box position="absolute" width="28px" height="28px" />}
            </Header>
            {renderStatusBadge(post, props.onStatusBadgeClick)}
            <Shadow
                isVideo={post.content[activeIndex]?.type === 'video'}
                onClick={onExpand}
                isMobile={isMobile}
                isVisible={isCardActive}
                isExpanded={isExpanded}
                isProductExpanded={isProductExpanded}
            />
            <Carousel
                onClick={onExpand}
                count={post.content.length}
                activeIndex={activeIndex}
                onSlideChange={onSlideChange}
                renderItem={(index) => {
                    const slide = post.content[index];

                    return (
                        <PostMediaCover
                            key={index}
                            ratio={ratio}
                            minWidth="100%"
                            isMobile={isMobile}
                            maxHeightOffset={maxHeightOffset}
                        >
                            {slide.type === 'image' && (
                                <Retry>
                                    <Image
                                        width="100%"
                                        height="100%"
                                        src={buildImageSource(slide.media, 'card')}
                                        srcSet={buildImageSourceSet(slide.media, 'card')}
                                    />
                                </Retry>
                            )}
                            {slide.type === 'video' && (
                                <Video
                                    media={slide.media[0]}
                                    onVideoMuteChange={onVideoMuteChange}
                                    onVideoEnd={onVideoEnd}
                                    onVideoStart={onVideoStart}
                                    onVideoPlay={onVideoPlay}
                                    onVideoPause={onVideoPause}
                                    soundContainer={
                                        index === activeIndex ? soundContainer : undefined
                                    }
                                />
                            )}
                        </PostMediaCover>
                    );
                }}
            />
            <Box position="absolute" top={0} left={0} width="100%" zIndex={5} px={1.5} py={3 / 4}>
                <CarouselIndicators
                    variant="story"
                    activeIndex={activeIndex}
                    count={post.content.length}
                />
            </Box>
            <Content
                pointerEvents="none"
                background="linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.30) 70%)"
            >
                <Flex direction="column" position="absolute" right="8px" pointerEvents="auto">
                    <IconButton
                        dataTestId={DataTestId.LIKE}
                        onClick={(e) => {
                            e.stopPropagation();
                            onReaction(ReactionState.LIKE);
                        }}
                    >
                        <IconLike
                            dataTestId={
                                post.reactionState === 'like' ? DataTestId.FILLED : DataTestId.EMPTY
                            }
                            fontSize={24}
                            strokeWidth="2px"
                            stroke="#FFFFFF"
                            fill={post.reactionState === 'like' ? '#FFFFFF' : 'transparent'}
                        />
                    </IconButton>

                    {post.business ? (
                        <IconButton
                            dataTestId={DataTestId.DISLIKE}
                            onClick={(e) => {
                                e.stopPropagation();
                                onReaction(ReactionState.DISLIKE);
                            }}
                        >
                            {post.reactionState === 'dislike' ? (
                                <IconDislikeFilled
                                    fontSize={24}
                                    strokeWidth="2px"
                                    fill="#FFFFFF"
                                    dataTestId={DataTestId.FILLED}
                                />
                            ) : (
                                <IconDislike
                                    fontSize={24}
                                    strokeWidth="2px"
                                    fill="#FFFFFF"
                                    dataTestId={DataTestId.EMPTY}
                                />
                            )}
                        </IconButton>
                    ) : null}

                    {!post.ownersData?.isOwner && (
                        <IconButton onClick={onBookmark} dataTestId={DataTestId.BOOKMARK}>
                            {post.saved ? (
                                <IconBookmarkFilled
                                    fontSize={24}
                                    color="#FFFFFF"
                                    dataTestId={DataTestId.FILLED}
                                />
                            ) : (
                                <IconBookmark
                                    fontSize={24}
                                    color="#FFFFFF"
                                    dataTestId={DataTestId.EMPTY}
                                />
                            )}
                        </IconButton>
                    )}

                    <IconButton onClick={onShare} dataTestId={DataTestId.SHARE}>
                        <IconShare color="#FFFFFF" />
                    </IconButton>
                </Flex>
                <AnimateFooter
                    post={post}
                    postElement={postRef.current}
                    isExpanded={isExpanded}
                    onSubscribe={onSubscribe}
                    onClickOutClick={onClickOutClick}
                    onExpand={onExpand}
                    isProductExpanded={isProductExpanded}
                    onProductButtonClick={onProductButtonClick}
                />
            </Content>
        </Root>
    );
}

export const PostCardSkeleton = () => (
    <Root>
        <Skeleton width="100%" variant="rect" ratio={1}>
            <Content width="100%" height="100%" justifyContent="flex-end" direction="column">
                <AnimateFooterSkeleton />
            </Content>
        </Skeleton>
    </Root>
);
